import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";

import AuthLayout from "@/components/AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { errorMessage } from "@/lib/errors";

/** Shared by ResetPassword and SetPassword: same form, different endpoint and copy. */
export function PasswordForm({ title, subtitle, buttonText, submitToken }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const token = params.get("token") ?? "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return setError("Use at least 8 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    setBusy(true);
    setError("");
    try {
      await submitToken(token, password);
      const user = await refresh();
      navigate(user && !user.profile_complete ? "/CompleteProfile" : "/Events", { replace: true });
    } catch (err) {
      setError(errorMessage(err, "That link is no longer valid. Request a new one from the sign-in page."));
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <AuthLayout title={title} subtitle="This link is missing its token.">
        <p className="text-sm text-center text-gray-600">
          Request a new link from the{" "}
          <Link to="/ForgotPassword" className="text-indigo-600 font-medium hover:underline">
            forgot password
          </Link>{" "}
          page.
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={title} subtitle={subtitle}>
      <form onSubmit={submit} className="space-y-4">
        {error && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}
        <div className="space-y-1.5">
          <Label htmlFor="password">New Password</Label>
          <Input id="password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="At least 8 characters" autoFocus />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="confirm">Confirm Password</Label>
          <Input id="confirm" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
        </div>
        <Button type="submit" disabled={busy} className="w-full bg-indigo-600 hover:bg-indigo-700">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : buttonText}
        </Button>
      </form>
    </AuthLayout>
  );
}

export default function ResetPassword() {
  return <PasswordForm title="Choose a new password" subtitle="Then you'll be signed in right away." buttonText="Reset password" submitToken={api.auth.reset} />;
}
